import { useCallback, useEffect, useRef, useState } from "react";
// Components
import { FileInfo } from "../FileInfo";
import { Toasts } from "view/components/Toasts";
import { AttachImageCard } from "../ImageCard";
import Head from "view/components/Head";
// Store Utils
import { IfetchFormDatails } from "store/formDetails/initialState";
import { closeDataPortalDrawer } from "store/modals/reducer.actions";
import { RootState } from "store";
import { useSelector, useDispatch } from "react-redux";
// Libs
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useLocation } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import moment from "moment";
import * as Yup from "yup";
import apiLibrary from "services/api";
import { fetchDataPortalDetailAction } from "store/dataPortalDetail";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { IfetchPortalDetail } from "store/dataPortalDetail/initialState";
import { resetDataPortalState } from "store/dataPortalDetail/reducer.actions";
import { fetchDataPortalsAction } from "store/dataportal";
import { HandlerComponent } from "view/pages/Forms/EditFormDetails/Components/SurveyJs/helperComponents";
import { DarkMode } from "store/darkMode/initialState";

interface SelectOption {
  label: string;
  value: string;
}

interface LocationCoords {
  lat: string | number;
  long: string | number;
}

interface DataPortalFormValues {
  author: string;
  category: SelectOption | null;
  description: string;
  fileId: number;
  link: string;
  location: LocationCoords;
  materialCreationDate: string;
  organization: string;
  publisher: string;
  specieId: SelectOption | null;
  status: SelectOption | null;
  title: string;
}

export const DataPortalFormDetails = () => {
  const [initialValues, setInitialValues] = useState<DataPortalFormValues>({
    author: "",
    category: null,
    description: "",
    fileId: 0,
    link: "",
    location: {
      lat: "",
      long: "",
    },
    materialCreationDate: "",
    organization: "",
    publisher: "",
    specieId: null,
    status: null,
    title: "",
  });

  const [isNextPressed, setIsNextPressed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { addEditFileDataPortalModal } = useSelector(
    (state: RootState) => state.modals
  );

  const { data } = useSelector<RootState, IfetchPortalDetail>(
    (state) => state.DetailDataPortal
  );

  const location = useLocation();
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();

  const queryParams = new URLSearchParams(location.search);
  const formId = queryParams.get("formId") || "-1";

  // Yup validation schema
  const currentValidationSchema = Yup.object().shape({
    author: Yup.string(),
    category: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    description: Yup.string()
      .required("Description is required")
      .max(6000, "Description cannot exceed 6000 characters"),
    fileId: Yup.number(),
    link: Yup.string().url().nullable(),
    location: Yup.object().shape({
      lat: Yup.number().nullable(),
      long: Yup.number().nullable(),
    }),
    materialCreationDate: Yup.date()
      .nullable()
      .test(
        "startDate",
        "Start date must be before end date",
        function (value) {
          const endDate = this.parent.endDate;
          return endDate && value ? moment(value).isBefore(endDate) : true;
        }
      ),
    organization: Yup.string(),
    publisher: Yup.string(),
    specieId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    status: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    title: Yup.string().required("Title is required"),
  });

  // Handle drawer close
  const handleClose = (resetState?: boolean) => {
    dispatch(closeDataPortalDrawer());
  };

  const { isDarkMode } = useSelector<RootState, DarkMode>(
    (state) => state?.darkMode
  );

  // Handle submit
  const handleSubmit = async (values: any, actions: FormikHelpers<any>) => {
    try {
      let formValues = {
        ...values,
        specieId: values?.specieId?.value || null,
        category: values?.category?.label || "",
        status: values?.status?.label || "",
        materialCreationDate: values.materialCreationDate
          ? moment.utc(values.materialCreationDate).format("YYYY-MM-DD")
          : "",
      };
      if (!addEditFileDataPortalModal?.isEdit) {
        // Remove keys with falsy values (null, undefined, empty string)
        formValues = Object.fromEntries(
          Object.entries(formValues).filter(([_, v]) => v != null && v !== "")
        );
      }

      let res;
      if (addEditFileDataPortalModal?.isEdit) {
        res = await apiLibrary.DataPortal.editDataPortal(
          addEditFileDataPortalModal.data.id,
          formValues
        );
      } else {
        res = await apiLibrary.DataPortal.addDataPortal(formValues);
      }
      dispatch(fetchDataPortalsAction());
      Toasts.success(res.message);
      handleClose();
    } catch (error: any) {
      Toasts.error(error?.response?.data?.message || "An error occurred");
    } finally {
      actions.setSubmitting(false);
    }
  };

  // Fetch details if editing
  useEffect(() => {
    if (
      addEditFileDataPortalModal?.isOpen &&
      addEditFileDataPortalModal?.isEdit
    ) {
      dispatch(fetchDataPortalDetailAction(addEditFileDataPortalModal.data.id));
    }
  }, [addEditFileDataPortalModal?.isOpen, addEditFileDataPortalModal?.isEdit]);

  // Set form values when data is fetched
  useEffect(() => {
    if (data) {
      setInitialValues({
        author: data.author || "",
        category: data.category
          ? { label: data.category, value: data.category }
          : null,
        description: data.description || "",
        fileId: data.id || 0,
        link: "", // update if applicable
        location: {
          lat: data.location?.coordinates[1] || "",
          long: data.location?.coordinates[0] || "",
        },
        materialCreationDate: data.materialCreationDate || "",
        organization: data.organizationId || "",
        publisher: data.publisher || "",
        specieId: data?.specie?.id
          ? { value: data?.specie?.id, label: data?.specie?.name }
          : null,

        status: data.status ? { label: data.status, value: data.status } : null,
        title: data.title || "",
      });
    }
  }, [data]);

  useEffect(() => {
    return () => {
      dispatch(resetDataPortalState());
    };
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={currentValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        setFieldValue,
      }) => (
        <SwipeableDrawer
          anchor="right"
          open={addEditFileDataPortalModal?.isOpen}
          onClose={() => handleClose(true)}
          onOpen={() => {}}
          PaperProps={{
            sx: {
              backgroundColor: isDarkMode ? "#21262d" : "#ffffff",
            },
          }}
        >
          <Box sx={{ width: 720 }} role="presentation">
            <Form className="w-full flex flex-col">
              <Head title={addEditFileDataPortalModal?.isOpen ? `Files` : ""} />

              <div className="flex flex-col items-start justify-start w-full bg-white dark:bg-secondaryLight h-screen overflow-hidden">
                {!isNextPressed && !addEditFileDataPortalModal?.isEdit ? (
                  <AttachImageCard
                    handleClose={handleClose}
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                  />
                ) : (
                  <FileInfo
                    handleClose={handleClose}
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    isSubmitting={isSubmitting}
                    isEdit={addEditFileDataPortalModal?.isEdit}
                  />
                )}
                <button
                  onClick={() => {
                    if (!values.fileId && !values.link?.trim()) {
                      Toasts.error(
                        "Please upload a file or enter a valid link before proceeding."
                      );
                      return;
                    }
                    setIsNextPressed(true);
                  }}
                  disabled={
                    (!values.fileId && !values.link?.trim()) || isLoading
                  }
                  type="button"
                  className={`text-textWhite dark:text-inputText text-[15px] font-semibold rounded-full bg-primary hover:bg-primaryMid h-[40px] mt-2 block ms-auto px-6 mr-4 ${
                    (!values.fileId && !values.link?.trim()) || isLoading
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer opacity-100"
                  }`}
                >
                  <span>Next</span>
                </button>
              </div>
            </Form>
          </Box>
        </SwipeableDrawer>
      )}
    </Formik>
  );
};
